import { NoteDto, QueryFactory, SortDirection } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useState } from 'react';
import styles from './NotesPanel.module.scss';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { NoteItem } from './NoteItem';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { EmptyBlock } from '@lmt/unicorn-shared/app/components/patients/profile/overview/components/EmptyBlock';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { ButtonColor } from '../../common/ui-kit/buttons/Button';
import { startOfToday, subDays } from 'date-fns';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import AddIcon from '@lmt/unicorn-shared/app/icons/add-content.svg?react';

function relevantNoteOrder(note: NoteDto) {
  if (note.urgent) {
    return !note.startDate ? 1 : 2;
  }
  return !note.startDate ? 3 : 4;
}

export const NotesPanel: React.FC = (props) => {
  const i18next = useScopedTranslation('dashboard.notes');
  const forms = useGlobalForms();
  const claims = useClaims();
  const [currentTab, setCurrentTab] = useState<'relevant' | 'passed'>(
    'relevant',
  );
  const notesQuery = QueryFactory.NoteQuery.useGetQuery(
    currentTab === 'relevant'
      ? {
          from: startOfToday(),
          sorting_SortField: nameofFactory<NoteDto>()('startDate'),
          pagination_Limit: 100,
        }
      : {
          endsTill: subDays(startOfToday(), 1),
          pagination_Limit: 100,
          sorting_SortField: nameofFactory<NoteDto>()('startDate'),
          sorting_SortDirection: SortDirection.Desc,
        },
    {
      select(data) {
        if (currentTab === 'relevant') {
          data.data.sort((a, b) => relevantNoteOrder(a) - relevantNoteOrder(b));
        }
        return data;
      },
    },
  );
  const notes = notesQuery.data?.data ?? [];

  return (
    <div className={styles.container} data-test-id="notes-container">
      <div className={styles.header}>
        <div className={styles.row}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>{i18next.t('title')}</div>
            <div className={styles.count} data-test-id="notes-count">
              {notes.length}
            </div>
          </div>
          <div>
            <UniLink
              icon={<AddIcon />}
              onClick={forms.openNoteCreating}
              color={ButtonColor.Primary}
              testId="add-note-link"
            />
          </div>
        </div>
      </div>
      <div className={styles.tabs}>
        <div
          className={styles.tab}
          data-selected={currentTab === 'relevant'}
          onClick={() => setCurrentTab('relevant')}
          data-test-id="notes-tab"
        >
          {i18next.t('relevant')}
        </div>
        <div
          className={styles.tab}
          data-selected={currentTab === 'passed'}
          onClick={() => setCurrentTab('passed')}
          data-test-id="notes-tab"
        >
          {i18next.t('passed')}
        </div>
      </div>
      <div className={styles.notes}>
        {notes.length === 0 && (
          <div className={styles.emptyBlockContainer}>
            <EmptyBlock
              title={''}
              text={i18next.t('empty')}
              buttonTitle={
                currentTab === 'passed' ? '' : i18next.t('add_note_button')
              }
              onClick={forms.openNoteCreating}
            />
          </div>
        )}
        {notes.map((note) => (
          <div key={note.id}>
            <NoteItem note={note} />
          </div>
        ))}
      </div>
    </div>
  );
};
